import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, Target, Shield, Users, Zap, Globe } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '../i18n/LanguageContext';

export default function About() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const values = [
    {
      icon: Heart,
      title: "Authenticité",
      description: "Nous croyons aux connexions authentiques basées sur la vraie compatibilité, pas sur les apparences superficielles."
    },
    {
      icon: Shield,
      title: "Sécurité",
      description: "Votre sécurité et votre vie privée sont notre priorité absolue. Protection RGPD, vérification des profils et modération active."
    },
    {
      icon: Zap,
      title: "Innovation",
      description: "Nous utilisons les technologies d'IA les plus avancées pour révolutionner l'expérience des rencontres en ligne."
    },
    {
      icon: Users,
      title: "Communauté",
      description: "Nous construisons une communauté bienveillante où chacun peut trouver sa moitié dans un environnement respectueux."
    }
  ];

  const stats = [
    { number: "5000+", label: "Utilisateurs Actifs" },
    { number: "85%", label: "Taux de Satisfaction" },
    { number: "4.8/5", label: "Note Moyenne" },
    { number: "1250+", label: "Couples Formés" }
  ];

  return (
    <>
      <Helmet>
        <title>À Propos | A-More AI - Notre Mission et Vision</title>
        <meta name="description" content="Découvrez A-More AI, l'application de rencontre qui utilise l'intelligence artificielle pour créer des connexions authentiques. Notre mission, nos valeurs et notre équipe." />
        <meta name="keywords" content="à propos A-More AI, mission dating IA, équipe rencontre, vision application rencontre, qui sommes-nous" />
        <link rel="canonical" href={`${window.location.origin}/${language}/about`} />
      </Helmet>

      <div className="min-h-screen py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>

          {/* Hero Section */}
          <div className="glass-card p-8 mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <Heart className="w-16 h-16 text-pink-500 fill-pink-500 animate-pulse" />
            </div>
            <h1 className="text-4xl font-bold gradient-text mb-4">À Propos de A-More AI</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Nous révolutionnons les rencontres en ligne en utilisant l'intelligence artificielle 
              pour créer des connexions authentiques basées sur la compatibilité réelle des personnalités.
            </p>
          </div>

          {/* Mission Section */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Target className="w-8 h-8 text-pink-500" />
              <h2 className="text-2xl font-bold text-gray-800">Notre Mission</h2>
            </div>
            <p className="text-gray-600 leading-relaxed mb-4">
              Chez A-More AI, nous pensons que trouver l'amour ne devrait pas être une question de chance ou d'apparences. 
              Notre mission est d'utiliser la puissance de l'intelligence artificielle pour analyser en profondeur les 
              personnalités, les valeurs et les aspirations de chacun afin de créer des connexions significatives et durables.
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              Nous voulons que chaque personne puisse trouver quelqu'un qui la comprend vraiment, qui partage ses valeurs 
              et avec qui elle peut construire une relation authentique. C'est pourquoi nous avons développé un système 
              de matching basé sur Claude AI, une technologie d'intelligence artificielle de pointe qui va bien au-delà 
              des algorithmes traditionnels de dating.
            </p>
          </div>

          {/* Values Section */}
          <div className="glass-card p-8 mb-8">
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-8 h-8 text-pink-500" />
              <h2 className="text-2xl font-bold text-gray-800">Nos Valeurs</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <div key={index} className="p-6 bg-gradient-to-br from-pink-50 to-orange-50 rounded-xl">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-3 bg-white rounded-lg shadow-sm">
                        <Icon className="w-6 h-6 text-pink-500" />
                      </div>
                      <h3 className="font-bold text-gray-800">{value.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm">{value.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Technology Section */}
          <div className="glass-card p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Notre Technologie</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Claude AI - L'IA au Service de l'Amour</h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous utilisons Claude, une intelligence artificielle développée par Anthropic, reconnue pour sa 
                  capacité à comprendre les nuances du langage humain et à analyser des concepts complexes comme 
                  la personnalité et les valeurs. Cette technologie nous permet d'aller bien au-delà des simples 
                  questionnaires à choix multiples pour vraiment comprendre qui vous êtes.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Analyse Multi-Dimensionnelle</h3>
                <p className="text-gray-600 leading-relaxed">
                  Notre système analyse votre profil sur 8 dimensions clés : vos valeurs fondamentales, votre style 
                  de communication, vos objectifs de vie, vos centres d'intérêt, votre vision de la relation idéale, 
                  votre approche de la gestion des conflits, votre expression émotionnelle et vos attentes relationnelles. 
                  Cette analyse approfondie garantit des matchs de haute qualité.
                </p>
              </div>
              <div>
                <h3 className="font-bold text-gray-800 mb-2">Apprentissage Continu</h3>
                <p className="text-gray-600 leading-relaxed">
                  Grâce au système de notation sociale et aux feedbacks des utilisateurs, notre IA améliore 
                  constamment ses recommandations pour vous proposer des profils toujours plus compatibles.
                </p>
              </div>
            </div>
          </div>

          {/* Stats Section */}
          <div className="glass-card p-8 mb-8 bg-gradient-to-r from-pink-500 to-orange-400">
            <h2 className="text-2xl font-bold text-white text-center mb-8">A-More AI en Chiffres</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-white opacity-90 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Team Section */}
          <div className="glass-card p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Notre Équipe</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              A-More AI a été fondée par une équipe passionnée de développeurs, data scientists et experts en 
              psychologie relationnelle. Nous combinons expertise technique et compréhension humaine pour créer 
              une expérience de dating unique.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Notre équipe travaille 24/7 pour améliorer l'application, modérer les contenus, assurer la sécurité 
              de nos utilisateurs et répondre à vos questions. Nous sommes à votre écoute !
            </p>
          </div>

          {/* Contact CTA */}
          <div className="glass-card p-8 text-center">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Rejoignez l'Aventure</h2>
            <p className="text-gray-600 mb-6">
              Prêt à trouver l'amour grâce à l'intelligence artificielle ?
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/auth?mode=signup')}
                className="btn-primary px-8 py-3"
              >
                Commencer Gratuitement
              </button>
              <button
                onClick={() => navigate('/how-it-works')}
                className="btn-secondary px-8 py-3"
              >
                Découvrir Comment Ça Marche
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
